import { Q } from "@nozbe/watermelondb";
import { withDatabase } from "@nozbe/watermelondb/DatabaseProvider";
import { withObservables } from "@nozbe/watermelondb/react";
import { compose } from "recompose";
import { from, of } from "rxjs";
import Category from "../../../../utilities/data/model/Category";
import Department from "../../../../utilities/data/model/Department";
import Layout from "../../../../utilities/data/model/Layout";
import LayoutNode from "../../../../utilities/data/model/LayoutNode";
import {
  EnhancedLayoutReturn,
  GetLayoutObservablesParams,
  GetLayoutProductObservablesParams,
  LayoutProviderToEnhanceProps,
} from "./types";

function getLayoutObservables({
  layoutId,
  database,
}: GetLayoutObservablesParams) {
  // get layout from database and observe with nodes
  const layouts = layoutId
    ? database
        .get<Layout>("layouts")
        .query(Q.where("id", Q.eq(layoutId)))
        .observeWithColumns(["nodes"])
    : of(null);

  const layoutNodes = layoutId
    ? database
        .get<LayoutNode>("layout_nodes")
        .query(
          Q.where("layout_id", Q.eq(layoutId)),
          Q.and(Q.where("deleted_at", Q.eq(null)))
        )
        .observeWithColumns(["position", "product_id"])
    : of([]);

  // NOTE: filter node default labels can be precalculated if this is too slow
  // depends how many categories/departments there are
  const categories = database.get<Category>("categories").query().observe();
  const departments = database.get<Department>("departments").query().observe();

  return {
    layouts,
    layoutNodes,
    categories,
    departments,
  };
}

function getLayoutProductObservables({
  layoutNodes,
}: GetLayoutProductObservablesParams) {
  // get layout from database and observe with nodes
  const products = layoutNodes
    .filter((n) => !!n.productId)
    .map((layoutNode) => layoutNode.product.fetch());

  return {
    products: from(Promise.all(products)),
  };
}

export type LayoutProviderProps = {
  editLayout?: boolean;
  children?: React.ReactNode;
};

export const enhanceLayout = compose<
  EnhancedLayoutReturn,
  LayoutProviderToEnhanceProps
>(
  withDatabase,
  withObservables(["layoutId"], getLayoutObservables),
  withObservables(["layoutNodes"], getLayoutProductObservables)
);
